import type { ChatMsg, PeerInfo, PlaybackState, RoomInfo, Stroke } from '../lib/types';

export type TransportKind = 'webtransport' | 'websocket';
export type ConnStatus = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed' | 'error' | 'moved';

export type ServerMsg =
  | { t: 'welcome'; peerId: string; serverTime: number; room: RoomInfo; state: PlaybackState; peers: PeerInfo[]; chat: ChatMsg[]; strokes?: Stroke[] }
  | { t: 'state'; state: PlaybackState; serverTime: number }
  | { t: 'peers'; peers: PeerInfo[] }
  | { t: 'chat'; msg: ChatMsg }
  | { t: 'react'; from: string; name: string; emoji: string }
  | { t: 'typing'; peer: string; name: string; on: boolean }
  | { t: 'chatreact'; id: string; reactions: Record<string, string[]> }
  | { t: 'draw'; op: 'stroke'; stroke: Stroke }
  | { t: 'draw'; op: 'erase'; id: string }
  | { t: 'draw'; op: 'clear'; by: string; user?: string }
  | { t: 'event'; kind: 'joined' | 'left' | 'host' | 'catchup'; peer: string; name: string; text: string }
  | { t: 'room'; room: RoomInfo }
  | { t: 'error'; code: string; message: string }
  | { t: 'moved'; device: string; room: string }
  | { t: 'pong'; id: number; t0: number; t1: number; t2: number };

export type ClientMsg =
  | { t: 'cmd'; op: 'play' | 'pause' | 'seek'; position?: number }
  | { t: 'cmd'; op: 'rate'; rate: number }
  | { t: 'cmd'; op: 'load'; mediaId: string }
  | { t: 'report'; rtt: number; offset: number; ahead: number; drift: number; pos: number; stalled: boolean; quality?: string; late?: number; jumps?: number; startup?: number; segMbps?: number; segTtfb?: number; bwEst?: number; netType?: string; netDownlink?: number; netRtt?: number; probe1?: number; probe4?: number; probeTtfb?: number }
  | { t: 'chat'; text: string; file?: string }
  | { t: 'react'; emoji: string }
  | { t: 'typing'; typing: boolean }
  | { t: 'chatreact'; msgId: string; emoji: string }
  | { t: 'draw'; op: 'stroke'; target: string; stroke: Pick<Stroke, 'id' | 'color' | 'w' | 'pts' | 'done'> }
  | { t: 'draw'; op: 'erase'; msgId: string }
  | { t: 'draw'; op: 'clear' }
  | { t: 'presence'; sharing?: boolean; mic?: boolean }
  | { t: 'settings'; policy?: 'together' | 'independent'; locked?: boolean };

export type WorkerIn =
  | { type: 'connect'; wtUrl: string; certHash: string; wsUrl: string; token: string; room: string; prefer: 'auto' | TransportKind; deviceId: string; device: string }
  | { type: 'send'; msg: ClientMsg; datagram?: boolean }
  | { type: 'close' };

export type WorkerOut =
  | { type: 'status'; status: ConnStatus; transport?: TransportKind; detail?: string; wtFailed?: boolean }
  | { type: 'clock'; offset: number; rtt: number; minRtt: number; samples: number }
  | { type: 'msg'; msg: ServerMsg };

/** Wall-clock milliseconds with sub-ms precision, consistent across window and workers. */
export const preciseNow = () => performance.timeOrigin + performance.now();
